import { BEATS_PER_BAR } from '../../shared/tempo';
import {
  type Face,
  INK,
  MARK,
  MASK_BODY,
  type Pose,
  body,
  dot,
  eye,
  gladFace,
  line,
  shut,
  stroke,
} from '../face-kit';
import { TAU, type Ctx } from '../paint';
import { glintX, mouthOpen } from './larsen-moments';

const PILL = [-26, -44, 52, 74, 26] as const;
const YOKE = { y: -8, outer: 39, inner: 32, screw: 6.5, screwX: 35.5 };
const COLUMN = [-4.5, -40, 9, 66, 4.5] as const;
const SLATS = {
  from: -37,
  to: 24,
  step: 6.2,
  eyes: [-24, -11] as const,
  mouth: [3, 20] as const,
  inset: 6,
};
const SLAT_X = { grille: 8, mouth: 14 };
const FAINT = 'rgb(255 255 255 / 0.5)';
const SOFT = 'rgb(255 255 255 / 0.7)';
const GLAD_INK = 'rgb(20 20 20 / 0.25)';

function halfWidth(y: number): number {
  const [, top, width, height, radius] = PILL;
  const half = width / 2;
  const round = (center: number) =>
    Math.sqrt(Math.max(0, half * half - (y - center) * (y - center)));
  if (y < top + radius) {
    return round(top + radius);
  }
  return y > top + height - radius ? round(top + height - radius) : half;
}

function pill(ctx: Ctx): void {
  ctx.beginPath();
  ctx.roundRect(...PILL);
}

function screw(ctx: Ctx, x: number): void {
  ctx.moveTo(x + YOKE.screw, YOKE.y);
  ctx.arc(x, YOKE.y, YOKE.screw, 0, TAU);
}

function stand(ctx: Ctx): void {
  body(ctx, () => {
    ctx.beginPath();
    ctx.moveTo(-YOKE.outer, YOKE.y);
    ctx.arc(0, YOKE.y, YOKE.outer, Math.PI, 0, true);
    ctx.lineTo(YOKE.inner, YOKE.y);
    ctx.arc(0, YOKE.y, YOKE.inner, 0, Math.PI, false);
    ctx.closePath();
    screw(ctx, -YOKE.screwX);
    screw(ctx, YOKE.screwX);
    ctx.roundRect(-7, 28, 14, 16, 3);
    ctx.roundRect(-14, 42, 28, 8, 4);
  });
  for (const side of [-1, 1]) {
    dot(ctx, side * YOKE.screwX, YOKE.y, 2.4, MASK_BODY);
    ctx.lineWidth = 1.4;
    ctx.strokeStyle = MARK;
    ctx.stroke();
  }
  stroke(ctx, FAINT, 2, [-10, 46], [10, 46]);
}

function grille(ctx: Ctx, loopShare: number, color: string, glint: boolean): void {
  ctx.save();
  pill(ctx);
  ctx.clip();
  if (glint) {
    const sx = glintX(loopShare);
    ctx.beginPath();
    ctx.moveTo(sx - 10, -50);
    ctx.lineTo(sx + 6, -50);
    ctx.lineTo(sx + 36, 40);
    ctx.lineTo(sx + 20, 40);
    ctx.closePath();
    ctx.fillStyle = 'rgb(255 255 255 / 0.07)';
    ctx.fill();
  }
  const [eyesFrom, eyesTo] = SLATS.eyes;
  const [mouthFrom, mouthTo] = SLATS.mouth;
  for (let y = SLATS.from; y <= SLATS.to; y += SLATS.step) {
    if (y > eyesFrom && y < eyesTo) {
      continue;
    }
    const half = halfWidth(y) - SLATS.inset;
    const x0 = y > mouthFrom && y < mouthTo ? SLAT_X.mouth : SLAT_X.grille;
    if (half > x0 + 2) {
      for (const side of [-1, 1]) {
        stroke(ctx, color, 2.4, [side * x0, y], [side * half, y]);
      }
    }
  }
  ctx.restore();
}

function column(ctx: Ctx): void {
  ctx.beginPath();
  ctx.roundRect(...COLUMN);
  const light = ctx.createLinearGradient(-4, 0, 4, 0);
  light.addColorStop(0, MASK_BODY);
  light.addColorStop(0.5, '#4a4a4a');
  light.addColorStop(1, MASK_BODY);
  ctx.fillStyle = light;
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = SOFT;
  ctx.stroke();
}

function eyes(ctx: Ctx, pose: Pose): void {
  for (const side of [-1, 1]) {
    if (shut(pose)) {
      eye(ctx, pose, side * 14.5, -17, 5);
      continue;
    }
    line(ctx, 5, [side * 23, -28], [side * 8, -22]);
    ctx.save();
    ctx.translate(side * 14.5, -17);
    ctx.rotate(side * 0.26);
    ctx.beginPath();
    ctx.ellipse(0, 0, 7.5, 3.4, 0, 0, TAU);
    ctx.fillStyle = MARK;
    ctx.fill();
    ctx.restore();
  }
}

function mouth(ctx: Ctx, beats: number): void {
  const open = mouthOpen(beats);
  ctx.beginPath();
  ctx.ellipse(0, 12, 11, open, 0, 0, TAU);
  ctx.fillStyle = INK;
  ctx.fill();
  ctx.lineWidth = 3.2;
  ctx.strokeStyle = MARK;
  ctx.stroke();
  ctx.save();
  ctx.beginPath();
  ctx.ellipse(0, 12, 9, open - 1.5, 0, 0, TAU);
  ctx.clip();
  ctx.beginPath();
  for (let index = 0; index <= 10; index += 1) {
    const x = -9 + index * 1.8;
    const y = 12 + (index % 2 === 1 ? -1 : 1) * (open - 3) * 0.7;
    if (index === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  }
  ctx.lineWidth = 1.6;
  ctx.strokeStyle = SOFT;
  ctx.stroke();
  ctx.restore();
}

function larsen(ctx: Ctx, pose: Pose): void {
  stand(ctx);
  body(ctx, () => {
    pill(ctx);
  });
  grille(ctx, pose.t, FAINT, true);
  column(ctx);
  eyes(ctx, pose);
  mouth(ctx, pose.t * BEATS_PER_BAR);
}

function larsenGlad(ctx: Ctx): void {
  grille(ctx, 0, GLAD_INK, false);
  gladFace(ctx, { eyeY: -16, eyeX: 12, mouthY: 6, mouthWidth: 12, part: 'smile' });
}

export const COLUMN_AT: readonly [number, number] = [0, -7];
export const FOOT_AT: readonly [number, number] = [0, 50];

export const LARSEN_FACE: Face = {
  draw: larsen,
  glad: larsenGlad,
  frames: 16,
  loopBeats: 4,
  boss: true,
};
